import { Link } from "react-router-dom";
import {
  Printer, Download, ArrowLeft, ArrowRight, AlertTriangle, Boxes, ShieldCheck, Timer,
  CheckCircle2, TrendingDown, Recycle, Landmark, Scale,
} from "lucide-react";
import { IMAGES, COMPANY } from "../lib/data";

/* bold-parser */
const B = ({ t, cls = "text-ink font-semibold" }) => (
  <>
    {t.split(/(\*\*[^*]+\*\*)/g).map((s, i) =>
      s.startsWith("**") && s.endsWith("**")
        ? <strong key={i} className={cls}>{s.slice(2, -2)}</strong>
        : <span key={i}>{s}</span>
    )}
  </>
);

const FOOTPRINT_CUT = 30;

const PAINS = [
  "**Ballooning CapEx** from single-use, low-quality composite mats.",
  "**High annual replacement rates** from on-site breakage.",
  "**Expensive landfill disposal** fees — every cycle.",
  "Buying **volume** to cover **short-lived** assets.",
  "Budget scales with **breakage — not need.**",
];

const SOLUTIONS = [
  {
    icon: Boxes,
    title: "Portfolio Footprint Optimization",
    rows: [
      "**High utilization** & **fast turn times.**",
      "**Buy fewer assets** for the same footprint.",
      `**−${FOOTPRINT_CUT}% inventory** across a 3-yr pipeline.`,
      "Capital works **harder, not bigger.**",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Zero-Replacement Lifespan",
    rows: [
      "**0% failure/breakage** over the lifecycle.",
      "**No recurring** secondary purchase orders.",
      "**Disposal fees: eradicated.**",
      "Predictable, **capitalizable** asset.",
    ],
  },
  {
    icon: Timer,
    title: "Labor Compression Savings",
    rows: [
      "**Fast-install** locking system.",
      "**Slashes field labor** install hours.",
      "**Lowers mobilization bids.**",
      "Faster **crew turnover** between sites.",
    ],
  },
];

const OUTCOMES = [
  { icon: TrendingDown, v: `−${FOOTPRINT_CUT}%`, l: "compressed CapEx & inventory" },
  { icon: Recycle, v: "$0", l: "disposal & re-order costs" },
  { icon: Landmark, v: "10+ yrs", l: "capital asset longevity" },
];

export default function SourcingBrief() {
  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      {/* top bar */}
      <div className="sticky top-0 z-40 bg-ink text-white border-b border-white/10 print:hidden">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/products/big-bad-mats" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to product
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-white/50">Global Sourcing Brief · One-Page</span>
            <a href="/big-bad-mats-sourcing-brief.pdf" download data-testid="download-sourcing-brief" className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2 text-sm font-semibold hover:border-flame hover:text-flame transition-colors">
              <Download className="h-4 w-4" /> Download PDF
            </a>
            <button onClick={() => window.print()} data-testid="print-sourcing-brief" className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors">
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>
      </div>

      {/* ===== A4 LANDSCAPE SHEET ===== */}
      <div className="py-8 print:py-0 flex justify-center">
        <div
          data-testid="sourcing-brief-sheet"
          className="bg-white shadow-2xl print:shadow-none w-[297mm] h-[210mm] p-[11mm] flex flex-col"
          style={{ fontSize: "10px" }}
        >
          {/* header */}
          <header className="flex items-start justify-between border-b-2 border-ink pb-3">
            <div className="max-w-[66%]">
              <div className="flex items-center gap-2 mb-1.5">
                <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
                <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVEST</span>
                <span className="ml-2 border border-flame/40 bg-flame/10 text-flame px-2 py-0.5 text-[8px] uppercase tracking-[0.18em]">For the Director of Procurement / Global Sourcing · EPCM & Enterprise</span>
              </div>
              <h1 className="font-display font-black tracking-tight text-ink leading-[1] text-[31px]">
                Slash Lifecycle Spend. Turn Mats into Capital.
              </h1>
              <p className="mt-2 text-charcoal leading-snug text-[11px]">
                Stop optimizing <span className="font-semibold text-ink">lowest purchase price.</span> Optimize <span className="text-flame font-semibold">lowest Total Cost of Ownership</span> across the multi-year portfolio — and convert a short-term expense into a <span className="font-semibold text-ink">long-term capital asset.</span>
              </p>
            </div>
            <div className="w-[32%] bg-ink text-white p-3">
              <div className="text-[8px] uppercase tracking-[0.18em] text-white/50">The portfolio math</div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-center">
                <div>
                  <div className="font-display font-extrabold text-flame text-[22px] leading-none">−{FOOTPRINT_CUT}%</div>
                  <div className="text-[7.5px] uppercase tracking-wider text-white/50">CapEx footprint</div>
                </div>
                <div>
                  <div className="font-display font-extrabold text-flame text-[22px] leading-none">0%</div>
                  <div className="text-[7.5px] uppercase tracking-wider text-white/50">replacement rate</div>
                </div>
              </div>
            </div>
          </header>

          {/* body: pain (left) + financial triad (right) */}
          <div className="flex-1 grid grid-cols-5 gap-4 pt-4 min-h-0">
            {/* pain */}
            <div className="col-span-2 border border-border flex flex-col">
              <div className="bg-charcoal text-white px-4 py-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-flame" />
                <span className="font-display font-bold text-[12px] uppercase tracking-wider">Where the Budget Leaks</span>
              </div>
              <ul className="p-4 space-y-3 flex-1 flex flex-col justify-center">
                {PAINS.map((p, i) => (
                  <li key={p} className="flex items-start gap-2.5 text-charcoal text-[11px] leading-snug">
                    {i === 0
                      ? <TrendingDown className="h-4 w-4 text-flame flex-shrink-0 mt-0.5" />
                      : <span className="mt-1.5 h-1.5 w-1.5 bg-charcoal/40 flex-shrink-0" />}
                    <span><B t={p} cls="text-ink font-semibold" /></span>
                  </li>
                ))}
              </ul>
            </div>

            {/* solutions — the financial triad */}
            <div className="col-span-3 flex flex-col min-h-0">
              <div className="flex items-center gap-2 mb-2">
                <Scale className="h-4 w-4 text-flame" />
                <span className="font-display font-bold text-ink text-[12px] uppercase tracking-wider">The Financial Triad</span>
              </div>
              <div className="flex-1 grid grid-rows-3 gap-3">
                {SOLUTIONS.map((s, n) => (
                  <div key={s.title} className="border-2 border-flame flex flex-col">
                    <div className="bg-flame text-white px-4 py-1.5 flex items-center gap-2">
                      <span className="font-display font-black text-[15px] leading-none">{n + 1}</span>
                      <s.icon className="h-4 w-4" />
                      <span className="font-display font-bold text-[11.5px] uppercase tracking-wider">{s.title}</span>
                    </div>
                    <ul className="px-4 py-2 grid grid-cols-2 gap-x-4 gap-y-1.5 flex-1 content-center">
                      {s.rows.map((x) => (
                        <li key={x} className="flex items-start gap-2 text-charcoal text-[10.5px] leading-snug">
                          <CheckCircle2 className="h-3.5 w-3.5 text-flame flex-shrink-0 mt-0.5" />
                          <span><B t={x} /></span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* financial outcome breakout */}
          <div className="mt-4 bg-ink text-white px-6 py-3">
            <div className="flex items-center gap-2">
              <Landmark className="h-4 w-4 text-flame" />
              <span className="font-display font-bold text-[13px] uppercase tracking-wider">The Financial Outcome — ROI Framework</span>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-3">
              {OUTCOMES.map((o) => (
                <div key={o.l} className="bg-white/5 border border-white/10 px-4 py-2.5 flex items-center gap-3">
                  <o.icon className="h-6 w-6 text-flame flex-shrink-0" />
                  <div>
                    <div className="font-display font-extrabold text-flame text-[19px] leading-none">{o.v}</div>
                    <div className="text-white/60 text-[9.5px] mt-0.5">{o.l}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA footer */}
          <div className="mt-3 flex items-center justify-between gap-4 border-t border-border pt-3">
            <div>
              <div className="font-display font-extrabold text-ink text-[15px] leading-tight">Model the portfolio — not just the purchase order.</div>
              <div className="text-muted-foreground text-[10px] mt-0.5">{COMPANY.phone} · {COMPANY.email} · {COMPANY.serviceArea}</div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 print:hidden">
              <Link to="/request-quote" className="inline-flex items-center gap-1.5 bg-flame text-white px-4 py-2.5 font-semibold text-[11px] hover:bg-flame/90 transition-colors">
                Custom Lifecycle TCO &amp; ROI Framework <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <Link to="/big-bad-mats-tco" className="inline-flex items-center gap-1.5 border border-border text-ink px-4 py-2.5 font-semibold text-[11px] hover:border-flame hover:text-flame transition-colors">
                See the TCO Framework
              </Link>
            </div>
          </div>
          <p className="mt-2 text-[8px] text-muted-foreground text-center">CapEx, replacement &amp; disposal figures are illustrative estimates — modeled against your project pipeline &amp; utilization during the TCO calculation. Not a quote, guarantee, or warranty of any specific result; not financial, engineering, or legal advice. © {new Date().getFullYear()} {COMPANY.legal}.</p>
        </div>
      </div>
    </div>
  );
}
